/**
 * RBAC context — loads /me ONCE and exposes the resolved identity to the whole app:
 * role, permissions, sees_money, hasPermission(), canSeeMoney().
 *
 * Money visibility is read STRICTLY from `sees_money` (the server's authoritative flag) — never
 * inferred from a missing money field. canSeeMoney() and the MoneyGate component both gate on this,
 * so the rule lives in one place.
 */

import { createContext, useContext, useMemo, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/api/client";
import type { Me, Permission, Role } from "@/api/types";

interface RbacValue {
  me: Me;
  role: Role;
  permissions: ReadonlySet<Permission>;
  seesMoney: boolean;
  hasPermission: (p: Permission) => boolean;
  canSeeMoney: () => boolean;
}

const RbacContext = createContext<RbacValue | null>(null);

/** Query key for /me — exported so a sign-out flow can invalidate it. */
export const ME_QUERY_KEY = ["me"] as const;

interface RbacProviderProps {
  children: ReactNode;
  /** Rendered while /me is loading. */
  fallback: ReactNode;
  /** Rendered if /me fails (not signed in / not staff / network). Receives the error. */
  onError: (error: unknown) => ReactNode;
}

export function RbacProvider({ children, fallback, onError }: RbacProviderProps) {
  const { data, isPending, isError, error } = useQuery({
    queryKey: ME_QUERY_KEY,
    queryFn: ({ signal }) => api.me(signal),
    staleTime: 5 * 60_000,
    retry: false, // a 401/403 should surface immediately, not retry-storm the gate
  });

  const value = useMemo<RbacValue | null>(() => {
    if (!data) return null;
    const permissions = new Set(data.permissions);
    return {
      me: data,
      role: data.role,
      permissions,
      seesMoney: data.sees_money,
      hasPermission: (p: Permission) => permissions.has(p),
      canSeeMoney: () => data.sees_money,
    };
  }, [data]);

  if (isPending) return <>{fallback}</>;
  if (isError || !value) return <>{onError(error)}</>;

  return <RbacContext.Provider value={value}>{children}</RbacContext.Provider>;
}

/** Access the resolved RBAC identity. Throws if used outside RbacProvider (programmer error). */
export function useRbac(): RbacValue {
  const ctx = useContext(RbacContext);
  if (!ctx) throw new Error("useRbac must be used within <RbacProvider>");
  return ctx;
}
